/**
 * Menu Layout store — drives the configurable left sidebar (Menu Builder).
 *
 * The menu CATALOG (which sections/items exist, default icons/titles and their
 * required permissions) is owned by App.vue and PUBLISHED here via setCatalog()
 * — this store never hardcodes the menu. It holds the EFFECTIVE layout overrides
 * for the current user (personal override -> company -> empty default) and
 * exposes tiny helpers App.vue uses to reorder / hide / rename / re-icon each
 * section and reorder / hide the items inside it.
 *
 * IMPORTANT — zero-risk fallback: overrides are only applied when the
 * "New Design" preset is active (branding.iconStyle === 'newdesign'). When it
 * is off, every helper reports the legacy behaviour (built-in order, nothing
 * hidden, original labels/icons), so the sidebar renders exactly as before.
 *
 * RBAC always wins: App.vue applies these overrides AFTER permission filtering,
 * so hiding an item here can never reveal one a role has no permission for.
 */
import { defineStore } from 'pinia'
import menuBuilderApi from '@/api/menuBuilder'
import { useBrandingStore } from '@/stores/branding'

export const useMenuLayoutStore = defineStore('menuLayout', {
  state: () => ({
    layout: { sections: [] },
    // Catalog published by App.vue: [{ key, title, icon, route, permission,
    //   items: [{ key, title, icon, permission }] }]
    catalog: [],
    source: 'default',
    canEditCompany: false,
    loaded: false,
    loading: false,
    // Live-preview override used by the builder while editing (not persisted).
    preview: null,
  }),

  getters: {
    // Only honour overrides under the New Design preset.
    active() {
      let isNewDesign = false
      try { isNewDesign = useBrandingStore().isNewDesignIcons } catch { /* ignore */ }
      return isNewDesign && (this.loaded || !!this.preview)
    },
    _sections(state) {
      return state.preview?.sections || state.layout?.sections || []
    },
    sectionByKey() {
      const map = {}
      for (const s of this._sections) map[s.key] = s
      return map
    },
  },

  actions: {
    _sec(key) { return this.sectionByKey[key] },

    // Is this top-level section hidden by the builder?
    sectionHidden(key) {
      if (!this.active) return false
      const s = this._sec(key)
      return !!s && s.hidden === true
    },
    // Effective sort order for a section (fallback keeps built-in order).
    sectionOrderOr(key, fallback) {
      if (!this.active) return fallback
      const s = this._sec(key)
      return s && typeof s.order === 'number' ? s.order : fallback
    },
    // Custom label override for a section (null => use original title).
    sectionLabel(key) {
      if (!this.active) return null
      const s = this._sec(key)
      return (s && s.label) ? s.label : null
    },
    // Custom icon override for a section (null => use original icon).
    sectionIcon(key) {
      if (!this.active) return null
      const s = this._sec(key)
      return (s && s.icon) ? s.icon : null
    },
    // Layout entries for a section's sub-items, or [] if not customized.
    sectionItemsLayout(key) {
      if (!this.active) return []
      const s = this._sec(key)
      return (s && Array.isArray(s.items)) ? s.items : []
    },

    async load() {
      if (this.loading) return
      this.loading = true
      try {
        const { data } = await menuBuilderApi.effective()
        if (data?.layout?.sections) this.layout = data.layout
        this.source = data?.source || 'default'
        this.canEditCompany = !!data?.can_edit_company
        this.loaded = true
      } catch (e) {
        // Endpoint missing / offline → keep empty layout, do not disturb the UI.
        this.loaded = true
        console.warn('[MenuLayout] load failed, using default:', e?.response?.status || e?.message)
      } finally {
        this.loading = false
      }
    },

    // App.vue pushes a serializable snapshot of its menuItems catalog here.
    setCatalog(sections) {
      this.catalog = Array.isArray(sections) ? sections : []
    },

    setPreview(layout) {
      this.preview = layout ? JSON.parse(JSON.stringify(layout)) : null
    },
    clearPreview() { this.preview = null },

    async save(scope, layout, userId = null) {
      const { data } = await menuBuilderApi.saveLayout(scope, layout, userId)
      if (data?.layout && (scope === 'me' || scope === 'company')) {
        this.layout = data.layout
        this.source = scope === 'me' ? 'user' : 'company'
      }
      this.clearPreview()
      return data
    },

    async reset(scope, userId = null) {
      await menuBuilderApi.resetLayout(scope, userId)
      this.clearPreview()
      await this.load()
    },
  },
})

export default useMenuLayoutStore
